'use strict';

import Swiper from 'swiper';
import { FreeMode, Mousewheel, Navigation, Pagination, Keyboard } from 'swiper/modules';

const SELECTOR = '.js-gallery';

export function init() {
    const elements = document.querySelectorAll(SELECTOR);

    if (!elements.length) {
        return;
    }

    elements.forEach(element => {
        const slidesContainerSelector = element.querySelector('.image-gallery__content');
        const slideDetailsSelector = element.dataset.gallerySlideDetailsSelector || '.swiper-slide-active .gallery-slide__content';

        const fixedBodyClass = element.dataset.galleryFixedBodyClass || 'image-gallery-fixed-body';
        const fullscreenClass = element.dataset.galleryFullscreenClass || 'image-gallery--fullscreen';

        const detailsContainer = element.querySelector(
            element.dataset.galleryDetailsContainerSelector || '.image-gallery__details'
        );

        const positionContainer = element.querySelector(
            element.dataset.galleryPositionContainerSelector || '.image-gallery__position'
        );

        const prevButtons = Array.from(element.querySelectorAll(
            element.dataset.galleryPrevButtonSelector || '.image-gallery__button--previous'
        ));

        const nextButtons = Array.from(element.querySelectorAll(
            element.dataset.galleryNextButtonSelector || '.image-gallery__button--next'
        ));

        const fullscreenButton = element.querySelector(
            element.dataset.galleryFullscreenButtonSelector || '.image-gallery__button--fullscreen'
        );

        const images = element.querySelectorAll(
            element.dataset.galleryImageSelector || '.gallery-slide__image'
        );

        const indexContainer = element.querySelector('.image-gallery__index');
        const totalSlides = element.querySelectorAll('.image-gallery__slide').length;
        const isSingleSlide = element.querySelectorAll('.image-gallery__slide').length === 1;

        if (isSingleSlide) {
            prevButtons.forEach(btn => btn.classList.add('disabled'));
            nextButtons.forEach(btn => btn.classList.add('disabled'));
        }

        let swiper = new Swiper(slidesContainerSelector, {
            init: false,
            modules: [
                FreeMode,
                Mousewheel,
                Navigation,
                Pagination,
                Keyboard
            ],
            slidesPerView: 'auto',
            simulateTouch: false,
            freeMode: {
                enabled: true,
            },
            mousewheel: {
                releaseOnEdges: true,
                forceToAxis: true,
                sensitivity: .8,
            },
            navigation: {
                prevEl: prevButtons,
                nextEl: nextButtons,
            },
            pagination: {
                el: positionContainer,
                type: 'fraction',
            },
        });

        swiper.on('init', () => {
            updateMeta();
            updateIndex();
        });

        swiper.on('activeIndexChange', () => {
            updateIndex();
        });

        swiper.on('slideChangeTransitionEnd', () => {
            updateMeta();
            updateIndex();
        });

        swiper.on('slideChange', updateNavigationButtons);


        swiper.init();

        if (fullscreenButton) {
            fullscreenButton.addEventListener('click', () => {
                if (isGalleryFullscreen()) {
                    endFullscreen();
                } else {
                    startFullscreen();
                }
            }, false);
        }

        document.addEventListener('keyup', event => {
            if (event.defaultPrevented) {
                return;
            }

            if (isGalleryFullscreen() === false) {
                return;
            }

            switch (event.key || event.code) {
                case 'Escape':
                case 'Esc':
                    endFullscreen();
                    break;
                case 'ArrowLeft':
                case 'Left':
                    swiper.slidePrev();
                    break;
                case 'ArrowRight':
                case 'Right':
                    swiper.slideNext();
                    break;
                default:
                    return;
            }
        });

        function updateMeta() {
            const slideDetailContainer = element.querySelector(slideDetailsSelector);

            if (!slideDetailContainer || !detailsContainer) {
                return;
            }

            detailsContainer.innerHTML = slideDetailContainer.innerHTML;
        }

        function updateNavigationButtons() {
            prevButtons.forEach(btn => btn.classList.toggle('disabled', swiper.isBeginning));
            nextButtons.forEach(btn => btn.classList.toggle('disabled', swiper.isEnd));
        }

        function updateIndex() {
            if (!indexContainer) return;
            const current =
            typeof swiper.realIndex === 'number'
            ? swiper.realIndex + 1
            : swiper.activeIndex + 1;
            indexContainer.textContent = `${current} / ${totalSlides}`;
            indexContainer.setAttribute('aria-label', `Image ${current} of ${totalSlides}`);
        }

        function isGalleryFullscreen() {
            return element.classList.contains(fullscreenClass);
        }

        let documentScrollTop = 0;

        function trapFocus(event) {
            const focusableElements = Array.from(element.querySelectorAll('button, a, input, [tabindex="0"]')).filter(el => !el.classList.contains('disabled'));
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            if (event.key === 'Tab') {
                if (event.shiftKey) {  // If Shift + Tab is pressed (backward tabbing)
                    if (document.activeElement === firstFocusable) {
                        lastFocusable.focus();  // Move focus to the last focusable element
                        event.preventDefault();
                    }
                } else {  // Regular Tab key (forward tabbing)
                    if (document.activeElement === lastFocusable) {
                        firstFocusable.focus();  // Move focus to the first focusable element
                        event.preventDefault();
                    }
                }
            }
        }

        function startFullscreen() {
            switchImages();
            element.classList.add(fullscreenClass);
            documentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            document.body.classList.add(fixedBodyClass);
            document.querySelector('.image-gallery__fullscreen-close').focus();
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', trapFocus);
        }

        function endFullscreen() {
            switchImages();
            element.classList.remove(fullscreenClass);
            document.body.classList.remove(fixedBodyClass);
            document.documentElement.scrollTop = document.body.scrollTop = documentScrollTop;
            document.body.style.overflow = '';
            document.removeEventListener('keydown', trapFocus);
        }

        function switchImages() {
            images.forEach(image => {
                const { fullscreenSrc } = image.dataset;

                if (!fullscreenSrc) {
                    return;
                }

                image.dataset.fullscreenSrc = image.src;
                image.src = fullscreenSrc;
            });
        }
    });
}
